import React, { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * Context for tracking barcodes across the entire session
 * Prevents duplicate barcodes within the same session across all holdings and items
 */
const BarcodeSessionContext = createContext();

/**
 * Hook to use the barcode session context
 * @returns {Object} The barcode session context value
 * @throws {Error} If used outside of BarcodeSessionProvider
 */
export const useBarcodeSession = () => {
  const context = useContext(BarcodeSessionContext);
  if (!context) {
    throw new Error('useBarcodeSession must be used within a BarcodeSessionProvider');
  }
  return context;
};

/**
 * Provider component for managing session-wide barcode tracking
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const BarcodeSessionProvider = ({ children }) => {
  // Use Set for O(1) lookup performance
  const [sessionBarcodes, setSessionBarcodes] = useState(new Set());

  /**
   * Add barcodes to the session tracking
   * @param {string[]} barcodes - Array of barcodes to add
   */
  const addBarcodes = useCallback((barcodes) => {
    if (!Array.isArray(barcodes)) return;

    const validBarcodes = barcodes.filter(barcode => barcode && typeof barcode === 'string' && barcode.trim() !== '');

    if (validBarcodes.length > 0) {
      setSessionBarcodes(prev => {
        const newSet = new Set(prev);
        validBarcodes.forEach(barcode => newSet.add(barcode.trim()));
        return newSet;
      });
    }
  }, []);

  /**
   * Remove barcodes from the session tracking
   * @param {string[]} barcodes - Array of barcodes to remove
   */
  const removeBarcodes = useCallback((barcodes) => {
    if (!Array.isArray(barcodes)) return;

    const validBarcodes = barcodes.filter(barcode => barcode && typeof barcode === 'string' && barcode.trim() !== '');

    if (validBarcodes.length > 0) {
      setSessionBarcodes(prev => {
        const newSet = new Set(prev);
        validBarcodes.forEach(barcode => newSet.delete(barcode.trim()));
        return newSet;
      });
    }
  }, []);

  /**
   * Check if a single barcode exists in the session
   * @param {string} barcode - Barcode to check
   * @returns {boolean} True if barcode exists
   */
  const checkBarcodeExists = useCallback((barcode) => {
    if (!barcode || typeof barcode !== 'string') return false;
    return sessionBarcodes.has(barcode.trim());
  }, [sessionBarcodes]);

  /**
   * Generate sequential barcode based on starting barcode and index
   * @param {string} startBarcode - Starting barcode pattern
   * @param {number} index - Index for sequential generation
   * @returns {string} Generated barcode
   */
  const generateSequentialBarcode = useCallback((startBarcode, index) => {
    if (!startBarcode || typeof startBarcode !== 'string') return '';

    const trimmedBarcode = startBarcode.trim();
    if (trimmedBarcode === '') return '';

    // Check if barcode ends with numbers
    const match = trimmedBarcode.match(/^(.*)(\d+)$/);
    if (match) {
      const prefix = match[1];
      const startNumber = parseInt(match[2], 10);
      const newNumber = startNumber + index;
      const originalLength = match[2].length;
      const paddedNumber = newNumber.toString().padStart(originalLength, '0');
      return `${prefix}${paddedNumber}`;
    } else {
      // If no trailing number, append sequential number with padding
      const paddedIndex = (index + 1).toString().padStart(3, '0');
      return `${trimmedBarcode}${paddedIndex}`;
    }
  }, []);

  /**
   * Validate a range of sequential barcodes for conflicts
   * @param {string} startingBarcode - Starting barcode pattern
   * @param {number} count - Number of barcodes to generate and check
   * @returns {Object[]} Array of conflict objects with index and barcode
   */
  const validateBarcodeRange = useCallback((startingBarcode, count) => {
    if (!startingBarcode || typeof startingBarcode !== 'string' || count <= 0) {
      return [];
    }

    const conflicts = [];
    const validCount = Math.max(1, parseInt(count, 10) || 1);

    for (let i = 0; i < validCount; i++) {
      const barcode = generateSequentialBarcode(startingBarcode, i);
      if (barcode && sessionBarcodes.has(barcode)) {
        conflicts.push({
          index: i,
          barcode,
          position: i + 1 // Human-readable position (1-based)
        });
      }
    }

    return conflicts;
  }, [sessionBarcodes, generateSequentialBarcode]);

  /**
   * Get all barcodes currently tracked in the session
   * @returns {string[]} Array of all session barcodes
   */
  const getSessionBarcodes = useCallback(() => {
    return Array.from(sessionBarcodes);
  }, [sessionBarcodes]);

  /**
   * Clear all barcodes from the session
   * Useful for resetting the session or clearing on form reset
   */
  const clearSessionBarcodes = useCallback(() => {
    setSessionBarcodes(new Set());
  }, []);

  /**
   * Get session statistics
   * @returns {Object} Statistics about current session
   */
  const getSessionStats = useCallback(() => {
    return {
      totalBarcodes: sessionBarcodes.size,
      isEmpty: sessionBarcodes.size === 0,
    };
  }, [sessionBarcodes]);

  /**
   * Restore barcodes to the session (alias for addBarcodes)
   * Used when undoing delete operations
   * @param {string[]} barcodes - Array of barcodes to restore
   */
  const restoreBarcodes = useCallback((barcodes) => {
    addBarcodes(barcodes);
  }, [addBarcodes]);

  const contextValue = {
    // State accessors
    sessionBarcodes: getSessionBarcodes(),
    sessionStats: getSessionStats(),

    // Barcode management functions
    addBarcodes,
    removeBarcodes,
    restoreBarcodes, // Added for semantic clarity in undo operations
    checkBarcodeExists,
    clearSessionBarcodes,

    // Validation functions
    validateBarcodeRange,
    generateSequentialBarcode,
  };

  return (
    <BarcodeSessionContext.Provider value={contextValue}>
      {children}
    </BarcodeSessionContext.Provider>
  );
};

BarcodeSessionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default BarcodeSessionContext;
